import { ShaderPass } from './ShaderPass';
import { GaussianPass } from './GaussianPass';
import { BackgroundShader } from './BackgroundShader';
import screen_quad_uv_vert from './shaders/screen_quad_uv_vert.glsl';
import groundreflection_draw_frag from './shaders/groundreflection_draw_frag.glsl';
import { GroundCommon } from './GroundCommon';
import { GroundFlags } from "./GroundFlags";
import { DEPTH_EPSILON } from "../globals";
import THREE from "three";

var GroundReflectionDrawShader = {
  uniforms: {
    tDiffuse: { type: "t", value: null }
  },

  vertexShader: screen_quad_uv_vert,
  fragmentShader: groundreflection_draw_frag
};

export class GroundReflection extends GroundCommon {
  _gaussianPass = undefined;
  _drawPass = undefined;
  _groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  _groundCenter = new THREE.Vector3(0, 0, 0);
  _reflCamera = undefined;
  _isGroundCulled = false;
  _clearColor = new THREE.Color(0, 0, 0);
  _clearPass = undefined;
  _useClearPass = false;
  _envMapBg = false;

  inTarget = undefined;
  outTarget = undefined;

  _needClear = true;

  _status = GroundFlags.GROUND_UNFINISHED;

  // param defaults
  _params = {
    color: new THREE.Color(1.0, 1.0, 1.0),
    alpha: 0.3,
    texScale: 0.5,
    blurRadius: 2,
    blurTexScale: 0.5,
    fadeAngle: Math.PI / 18
  };

  _scenesPerModel = [];
  _qScenes = undefined;

  _qSceneCount = 0;
  _qSceneIdx = 0;

  _MAX_PROCESS_FRAMES = 100;
  _maxScenesPerFrame = 0;

  constructor(renderer, width, height, params) {
    super();
    this._width = width || 512;
    this._height = height || 512;

    this._renderer = renderer;
    this._gl = this._renderer.getContext();

    // INITIALIZATION

    if (params) {
      for (var i in this._params) {
        this._params[i] = params[i] !== undefined ? params[i] : this._params[i];
      }
    }

    // init passes

    this._drawPass = new ShaderPass(GroundReflectionDrawShader);
    this._drawPass.material.blending = THREE.NoBlending;
    this._drawPass.material.depthTest = true;
    this._drawPass.material.depthWrite = false;
    // Put the screen-filling quad at the back of the view volume.
    // This is slightly dangerous, it could go "too far", so we put it at
    // -0.999999 to keep it from being on the razor's edge.
    // See https://jira.autodesk.com/browse/LMV-1262
    this._drawPass.scene.position.z = -(1.0 - 2 * DEPTH_EPSILON);

    if (params.clearPass) {
      this._clearPass = params.clearPass;
    } else {
      this._clearPass = new ShaderPass(BackgroundShader);
      this._clearPass.material.blending = THREE.NoBlending;
      this._clearPass.material.depthWrite = false;
      this._clearPass.material.depthTest = false;
    }

    // init targets
    this.setSize(this._width, this._height);

    this._gaussianPass.setAlpha(this._params.color);
    this._gaussianPass.setAlpha(this._params.alpha);
  }

  // PUBLIC FUNCTIONS
  // note: currently scale is not used
  setTransform(center, upDir, scale) {
    this._groundCenter = center;
    this._groundPlane.normal = upDir;
    this._groundPlane.constant = -center.dot(upDir);
  }

  cleanup() {
    if (this._gaussianPass) this._gaussianPass.cleanup();
    if (this.inTarget) this.inTarget.dispose();
    if (this.outTarget) this.outTarget.dispose();
  }

  setSize(width, height) {
    this._width = width;
    this._height = height;

    this.cleanup();

    // init targets

    this.inTarget = new THREE.WebGLRenderTarget(
      this._width * this._params.texScale,
      this._height * this._params.texScale,
      {
        magFilter: THREE.LinearFilter,
        minFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        stencilBuffer: false
      }
    );
    this.inTarget.texture.generateMipmaps = false;
    this.inTarget.name = "GroundReflection inTarget";

    // outTarget is where we're rendering the ground reflection image (without anything else)
    // and that we will merge with the regular rendering by putting it on a quad.
    this.outTarget = new THREE.WebGLRenderTarget(
      this._width * this._params.texScale,
      this._height * this._params.texScale,
      {
        magFilter: THREE.LinearFilter,
        minFilter: THREE.LinearFilter,
        format: THREE.RGBAFormat,
        stencilBuffer: false
      }
    );
    this.outTarget.texture.generateMipmaps = false;
    this.outTarget.name = "GroundReflection outTarget";

    // init gaussian pass

    if (!this._gaussianPass)
    this._gaussianPass = new GaussianPass(
      this._width * this._params.texScale * this._params.blurTexScale,
      this._height * this._params.texScale * this._params.blurTexScale,
      this._params.blurRadius,
      1.0, {
        hasAlpha: true,
        blending: true,
        flipUV: true
      });else

    this._gaussianPass.setSize(
      this._width * this._params.texScale * this._params.blurTexScale,
      this._height * this._params.texScale * this._params.blurTexScale);
  }

  updateCamera(camera) {
    // do not render if camera cannot see top of plane
    var camTarget;
    if (camera.isPerspective) {
      // For perspective camera, see if camera location -> point on plane
      // dotted with the plane's normal is positive. If so, camera is below
      // plane and ground can be culled.
      camTarget = this._groundCenter.clone();
    } else {
      // For orthographic camera, see if camera direction (target - position)
      // dotted with the plane's normal is positive. If so, camera is below
      // plane and ground can be culled.
      camTarget = camera.target.clone();
    }
    var camDir = camera.position.clone().sub(camTarget).normalize();
    var camAngle = Math.PI / 2 - camDir.angleTo(this._groundPlane.normal);
    this._isGroundCulled = camAngle < 0;

    if (this._isGroundCulled) return;

    // fade out
    if (this._params.fadeAngle > 0) {
      var fadeAmount = Math.min(this._params.fadeAngle, camAngle) / this._params.fadeAngle;
      this._gaussianPass.setAlpha(fadeAmount * this._params.alpha);
    }

    // construct reflected camera
    var reflMatrix = this._getReflectionMatrix(this._groundPlane);
    this._reflCamera = camera.clone();
    this._reflCamera.applyMatrix4(reflMatrix);
    // MAGIC: scale negative Y and flip UV gives us correct result without messing with face winding
    this._reflCamera.projectionMatrix.elements[5] *= -1;
    this._reflCamera.matrixWorldNeedsUpdate = true;

    // copy worldUpTransform
    if (camera.worldUpTransform)
    this._reflCamera.worldUpTransform = camera.worldUpTransform.clone();else

    this._reflCamera.worldUpTransform = new THREE.Matrix4();
  }

  renderIntoReflection(scene) {
    if (this._isGroundCulled) return;
    this._renderer.setRenderTarget(this.inTarget);

    this._renderer.render(scene, this._reflCamera);
  }

  resetScenesPerModel() {
    this._scenesPerModel = [];
    this._qScenes = undefined;
    this.setDirty();
  }

  prepareGroundReflection(groundShadow, viewerImpl, forceDraw, minScenesPerFrame, maxTime, ratio) {

    const modelQueue = viewerImpl.modelQueue();

    // if the ground reflection is finished, leave
    if (this._status !== GroundFlags.GROUND_UNFINISHED || modelQueue.isEmpty()) {
      // this call did not render it, so make sure the rendered status is set to finished.
      this._status = GroundFlags.GROUND_FINISHED;
      return maxTime;
    }

    // This will happen once the linear render list is replaced
    // by the BVH.
    const newScenesPerModel = modelQueue.getGeomScenesPerModel();
    this._needClear = this.needsClear(this._scenesPerModel, newScenesPerModel) || this._needClear;

    // Get a separate set of scenes (render batches) for us to traverse. Everything gets traversed.
    if (this._needClear) {
      this._needClear = false;

      // if it's not visible, don't bother
      // TODO this should really be tested once when making command list - if culled, don't even
      // do anything with reflection or with displaying ground plane.
      this.updateCamera(viewerImpl.camera);
      if (this.isGroundCulled()) {
        this._status = GroundFlags.GROUND_FINISHED;
        return maxTime;
      }

      this.clear();

      this._scenesPerModel = newScenesPerModel;
      this._qScenes = modelQueue.getGeomScenes();
      this._qSceneCount = this._qScenes.length;
      this._qSceneIdx = 0;
      if (minScenesPerFrame) {
        this._maxScenesPerFrame = Math.max(Math.ceil(this._qSceneCount / this._MAX_PROCESS_FRAMES), minScenesPerFrame);
      } else {
        this._maxScenesPerFrame = this._qSceneCount;
      }
      this._status = GroundFlags.GROUND_UNFINISHED;

    } else if (this._status !== GroundFlags.GROUND_UNFINISHED) {
      // finished, or just finished rendering last frame, or not visible;
      // set status to definitively finished.
      this._status = GroundFlags.GROUND_FINISHED;
      return maxTime;
    } else if (minScenesPerFrame === 0) {
      // render rest of scene, time permitting
      this._maxScenesPerFrame = this._qSceneCount;
    }

    // progressive draw into reflection
    var startTime, budget;

    if (maxTime) {
      startTime = performance.now();
      ratio = ratio === undefined ? 1.0 : ratio;
      budget = ratio * maxTime;
    }

    // TODO this is a bug in the old system: we should really use the BVH iterator here,
    // so that (a) it's draw with frustum culling (should be faster) and (b) transparency is
    // done properly from back to front. Need to get system to work properly before undertaking
    // this task.
    let retval;
    let i = 0;
    while (i < this._maxScenesPerFrame && this._qSceneIdx < this._qSceneCount) {
      // Note that we'll always render at least one batch here, regardless of time.
      // Not sure this is necessary, but it does avoid something going bad that causes
      // the timer to always fail and so get us caught in an infinite loop of calling
      // this method again and again.
      const qScene = this._qScenes[this._qSceneIdx++];

      if (!qScene) continue;

      i++;
      // passing forceVisible to WebGLRenderer.projectObject()
      qScene.forceVisible = true;
      // Note we render everything in the scene (render batch) to the ground plane,
      // so we don't have to worry about frustum culling, etc. - just blast through.
      this.renderIntoReflection(qScene);
      qScene.forceVisible = false;

      // check time, if used
      if (!maxTime) continue;

      const timeElapsed = performance.now() - startTime;
      // is time up and we're not done?
      if (budget < timeElapsed && this._qSceneIdx < this._qSceneCount) {
        // couldn't finish render in time
        this._status = GroundFlags.GROUND_UNFINISHED;
        retval = maxTime - timeElapsed;
        break;
      }
    }

    // Did we finish? We only reach this path if the maxObj limit is reached.
    if (this._qSceneIdx < this._qSceneCount) {
      this._status = GroundFlags.GROUND_UNFINISHED;
      // return time left, or 1, meaning we're not done.
      retval = maxTime ? maxTime - performance.now() + startTime : 1;
    }

    // Should we create an intermediate result for display?
    // Yes, if we're done rendering (retval is undefined), or if we're forcing it
    // because progressive rendering is on and this is the first tick's result.
    if (retval === undefined || forceDraw) {
      // We just finished, great, do the post-process
      this.postprocess(viewerImpl.camera, viewerImpl.matman());

      if (groundShadow && groundShadow.enabled) {
        viewerImpl.renderGroundShadow(this.outTarget);
      }

      this.renderReflection(viewerImpl.camera, viewerImpl.renderer().getColorTarget());

      // We give back a sign that it was *this* call that actually finished up.
      if (retval === undefined) {
        this._status = GroundFlags.GROUND_RENDERED;
      }
      return maxTime ? maxTime - performance.now() + startTime : 1;
    } else {
      return retval;
    }

  }

  // The way the reflection pass works is that we render the reflection
  // and blur it, etc. and the result is in outTarget. This method then
  // merges the color buffer and the reflection image by rendering the
  // reflection image on a screen-fillinq quad (well, a triangle) and
  // setting depth range so that the depth value is 0.999999+, i.e., at
  // the back of the scene.
  // This sort of merge draw means the color target can be left as-is,
  // no ping-ponging need occur, the reflection is put right into it.
  renderReflection(camera, target) {
    if (this._isGroundCulled) return;

    // Shove the quad with the reflection image to the back of the color buffer.
    // NOTE: depthRange does not appear to work on Chrome on Windows. See
    // _drawPass.scene.position.z for further corrective measure.
    // Also see https://jira.autodesk.com/browse/LMV-1262
    this._gl.depthRange(1.0 - 2.0 * DEPTH_EPSILON, 1.0 - DEPTH_EPSILON);
    this._drawPass.render(this._renderer, target, this.outTarget);
    // restore default range
    this._gl.depthRange(0, 1);
  }

  toggleEnvMapBackground(value) {

    this._envMapBg = value;
    this._clearPass.uniforms.envMapBackground.value = value;
  }

  postprocess(camera) {
    if (this._isGroundCulled) return;
    const renderer = this._renderer;
    const clearPass = this._clearPass;

    // clear outTarget with bg color
    if (this._useClearPass || this._envMapBg) {
      const uCamDir = clearPass.uniforms['uCamDir'].value || new THREE.Vector3();
      clearPass.uniforms['uCamDir'].value = camera.worldUpTransform ? camera.getWorldDirection(uCamDir).applyMatrix4(camera.worldUpTransform) : camera.getWorldDirection(uCamDir);
      clearPass.uniforms['uCamUp'].value = camera.worldUpTransform ? camera.up.clone().applyMatrix4(camera.worldUpTransform) : camera.up;
      clearPass.uniforms['uResolution'].value.set(this._width, this._height);
      clearPass.uniforms['uHalfFovTan'].value = Math.tan(THREE.Math.degToRad(camera.fov * 0.5));

      clearPass.render(renderer, this.outTarget);
      renderer.setRenderTarget(this.outTarget);
      renderer.clear(false, true, false);
    } else
    {
      renderer.setClearColor(this._clearColor, 1.0);
      renderer.setRenderTarget(this.outTarget);
      renderer.clear(true, true, false);
    }

    // blur inTarget with alpha blending over bg in outTarget
    this._gaussianPass.render(this._renderer, this.outTarget, this.inTarget);
  }

  clear() {
    // clear with bgColor otherwise there'll be outline problem
    // using the cheaper flat clear color in this case
    this._renderer.setClearColor(this._clearColor, 0);
    this._renderer.setRenderTarget(this.inTarget);
    this._renderer.clear(true, true, false);
    this._renderer.clearBlend();
  }

  // params are normalized clamped THREE.Vector3
  setClearColors(colorTop, colorBot, skipClearPass) {
    if (!colorBot) {
      this._clearColor.copy(colorTop);
      this._useClearPass = false;
    } else
    {
      this._clearColor.setRGB(
        0.5 * (colorTop.x + colorBot.x),
        0.5 * (colorTop.y + colorBot.y),
        0.5 * (colorTop.z + colorBot.z));

      // same logic as RenderContext.setClearColors
      this._useClearPass = !colorTop.equals(colorBot) && !skipClearPass;
    }

    if (this._useClearPass) {
      this._clearPass.uniforms.color1.value.copy(colorTop);
      this._clearPass.uniforms.color2.value.copy(colorBot);
    }
  }

  setEnvRotation(rotation) {
    this._clearPass.material.envRotationSin = Math.sin(rotation);
    this._clearPass.material.envRotationCos = Math.cos(rotation);
  }

  isGroundCulled() {
    return this._isGroundCulled;
  }

  getStatus() {
    return this._status;
  }

  setDirty() {
    this._needClear = true;
    this._status = GroundFlags.GROUND_UNFINISHED;
  }

  setColor(color) {
    this._gaussianPass.setColor(this._params.color);
    this._params.color.set(color);
  }

  setAlpha(alpha) {
    this._gaussianPass.setAlpha(this._params.alpha);
    this._params.alpha = alpha;
  }

  _getReflectionMatrix(plane) {
    var N = plane.normal;
    var C = plane.constant;
    return new THREE.Matrix4().set(
      1 - 2 * N.x * N.x, -2 * N.y * N.x, -2 * N.x * N.z, -2 * C * N.x,
      -2 * N.x * N.y, 1 - 2 * N.y * N.y, -2 * N.y * N.z, -2 * C * N.y,
      -2 * N.x * N.z, -2 * N.y * N.z, 1 - 2 * N.z * N.z, -2 * C * N.z,
      0, 0, 0, 1
    );
  }
}